import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

import { Badge } from '../Badge/Badge';
import { type BadgeTone } from '../Badge/Badge.tokens';

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * The tone the caller's domain maps this status to. The mapping itself
   * (`approved → success`) lives in the module that owns the status, never here
   * — a kit that knew `approved` would be a kit only this product can reuse.
   */
  tone: BadgeTone;
}

/** Per-tone dot colour. Kept beside the component because it IS the component. */
const DOT_TONE: Record<BadgeTone, string> = {
  neutral: 'bg-muted-foreground',
  info: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-destructive',
  accent: 'bg-accent',
};

/**
 * A badge with a leading status dot.
 *
 * The dot is `aria-hidden`: it repeats what the label already says, and a
 * screen reader announcing "circle approved" is worse than one announcing
 * "approved". Colour is never the only carrier of the state — the translated
 * label is.
 */
export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(function StatusBadge(
  { tone, children, className, ...props },
  ref,
) {
  return (
    <Badge ref={ref} tone={tone} className={className} {...props}>
      <span
        aria-hidden="true"
        data-testid="status-badge-dot"
        className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', DOT_TONE[tone])}
      />
      {children}
    </Badge>
  );
});
